import { createContext, useContext, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { getRuntimeConfig } from '../config/runtime-config';
import { authStore, type AuthState } from './auth-store';
import { createOidcClient } from './oidc-client';
import { getAuthClient, setAuthClient } from './registry';
import { broadcastAuth, onAuthBroadcast, sessionHint, topLevelSilentGuard } from './session-continuity';
import { OidcError, type LoginOptions, type OidcClient } from './types';

const AuthClientContext = createContext<OidcClient | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  client?: OidcClient;
  /** Seeds the auth store before mount (tests). A non-idle `status` is kept by the bootstrap. */
  initialState?: Partial<AuthState>;
  /** Called after a successful redirect callback with the `returnTo` captured at login time. */
  onAuthenticated?: (returnTo?: string) => void;
}

const CALLBACK_PARAMS = ['code', 'state', 'session_state', 'iss', 'error', 'error_description', 'error_uri'];

function stripCallbackParams(href: string): string {
  const url = new URL(href);
  for (const p of CALLBACK_PARAMS) url.searchParams.delete(p);
  return url.toString();
}

function isCallbackUrl(href: string): boolean {
  const params = new URL(href).searchParams;
  return (params.has('code') && params.has('state')) || params.has('error');
}

function currentLocation(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const { pathname, search, hash } = window.location;
  if (pathname === '/login' || pathname.startsWith('/auth/')) return undefined;
  return `${pathname}${search}${hash}`;
}

/**
 * Bootstraps the OIDC session (ADR-0022):
 *
 * 1. a redirect callback (`?code=&state=`) is completed — once, even when StrictMode mounts the effect twice;
 * 2. an in-memory session (same document) is kept;
 * 3. otherwise, when this browser had a session, it is restored from the identity provider's SSO cookie with a
 *    silent `prompt=none` request in a hidden iframe, and — if the iframe cannot answer (blocked frame, third-party
 *    cookie policy) — with a single top-level `prompt=none` redirect that returns to the same URL;
 * 4. only then is the user anonymous.
 *
 * Tokens are never persisted. Sign-in and sign-out are broadcast so other tabs follow.
 */
export function AuthProvider({ children, client: providedClient, initialState, onAuthenticated }: AuthProviderProps): ReactElement {
  const [client] = useState<OidcClient>(() => providedClient ?? createOidcClient(getRuntimeConfig().oidc));
  // The URL this document was opened with. The callback path strips the code from the address bar immediately, so
  // a StrictMode re-run of the effect must still see the callback (and join the exchange already in flight).
  const [openedWith] = useState(() => (typeof window !== 'undefined' ? window.location.href : ''));
  // Seed the store once, before the first render (tests / embedded hosts).
  useState(() => {
    if (initialState) authStore.setState(initialState);
    return undefined;
  });

  useEffect(() => {
    setAuthClient(client);
    // While a redirect callback completes, the callback path applies the tokens itself — after it has handed the
    // captured `returnTo` to the app. Applying them from the subscription first would let the callback page
    // navigate before it knows where to go.
    let completingCallback = false;
    const unsubscribe = client.subscribe((tokens, claims) => {
      const state = authStore.getState();
      if (completingCallback) return;
      if (tokens) {
        state.setTokens(tokens, claims);
        sessionHint.set();
        topLevelSilentGuard.reset();
      } else if (state.status === 'authenticated') {
        // The session could not be renewed: keep the screen, ask the user to sign in again.
        state.expire();
      }
    });
    const unsubscribeBroadcast = onAuthBroadcast((message) => {
      const state = authStore.getState();
      if (message.type === 'signed-out') {
        /*
         * Signed out elsewhere: this tab signs out, whatever the client does.
         *
         * The store is cleared first and the client's own teardown is allowed to fail. A throw in between
         * would leave the tab showing an authenticated shell after the user had signed out in another one,
         * which is the one outcome this handler exists to prevent.
         */
        authStore.getState().clear({ signedOut: true });
        try {
          client.clearLocalSession();
        } catch {
          /* nothing left to do here: the session this tab could act with is already gone */
        }
      } else if (message.type === 'signed-in' && state.status !== 'authenticated') {
        client
          .signInSilently()
          .then((tokens) => authStore.getState().setTokens(tokens, client.getClaims()))
          .catch(() => undefined);
      }
    });
    let cancelled = false;

    const completeCallback = async (href: string): Promise<void> => {
      authStore.getState().setStatus('authenticating');
      // The code is single-use: take it out of the address bar (and history) before anything else can reload it.
      if (isCallbackUrl(window.location.href)) window.history.replaceState(window.history.state, '', stripCallbackParams(window.location.href));
      completingCallback = true;
      try {
        const result = await client.handleCallback(href);
        if (cancelled) return;
        onAuthenticated?.(result.returnTo);
        authStore.getState().setTokens(result.tokens, client.getClaims());
        sessionHint.set();
        topLevelSilentGuard.reset();
        broadcastAuth({ type: 'signed-in' });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof OidcError && err.isInteractionRequired()) {
          // Answer to the top-level silent attempt: no SSO session. Not an error — sign in normally, then return.
          sessionHint.clear();
          onAuthenticated?.(err.returnTo);
          authStore.getState().setStatus('anonymous');
          return;
        }
        authStore.getState().setError(err instanceof OidcError ? err.code : err instanceof Error ? err.message : String(err));
      } finally {
        completingCallback = false;
      }
    };

    const restore = async (): Promise<void> => {
      authStore.getState().setStatus('authenticating');
      try {
        const tokens = await client.signInSilently();
        if (cancelled) return;
        authStore.getState().setTokens(tokens, client.getClaims());
        return;
      } catch (err) {
        if (cancelled) return;
        if (err instanceof OidcError && err.isInteractionRequired()) {
          sessionHint.clear();
          authStore.getState().setStatus('anonymous');
          return;
        }
      }
      // The iframe could not answer. One top-level attempt, then give up to the sign-in page.
      if (!topLevelSilentGuard.tried()) {
        topLevelSilentGuard.mark();
        try {
          const returnTo = currentLocation();
          await client.login({ prompt: 'none', ...(returnTo ? { returnTo } : {}) });
          return;
        } catch {
          /* discovery failed: fall through */
        }
      }
      if (!cancelled) authStore.getState().setStatus('anonymous');
    };

    const bootstrap = async (): Promise<void> => {
      const store = authStore.getState();
      if (typeof window !== 'undefined' && openedWith && isCallbackUrl(openedWith)) {
        await completeCallback(openedWith);
        return;
      }
      const tokens = client.getTokens();
      if (tokens && client.isAuthenticated()) {
        store.setTokens(tokens, client.getClaims());
        return;
      }
      if (store.status !== 'idle') {
        // A session seeded via `initialState` or `authStore.setTokens` (tests, embedded hosts) is kept as-is.
        return;
      }
      if (sessionHint.present() && typeof window !== 'undefined' && window.location.pathname !== '/logout') {
        await restore();
        return;
      }
      store.setStatus('anonymous');
    };
    void bootstrap();

    return () => {
      cancelled = true;
      unsubscribe();
      unsubscribeBroadcast();
      if (getAuthClient() === client) setAuthClient(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return <AuthClientContext.Provider value={client}>{children}</AuthClientContext.Provider>;
}

export interface UseAuthResult extends AuthState {
  /** Sends the browser to the identity provider's sign-in page; `hints` choose its language and workspace brand. */
  login: (returnTo?: string, hints?: Pick<LoginOptions, 'uiLocales' | 'tenantHint' | 'loginHint'>) => Promise<void>;
  loginWithPassword: (username: string, password: string) => Promise<void>;
  /** Resolves `true` when the browser is being redirected to the identity provider to end its session. */
  logout: () => Promise<boolean>;
  client: OidcClient | null;
}

/** Auth store state plus `login`/`logout` bound to the nearest OIDC client. */
export function useAuth(): UseAuthResult {
  const state = authStore();
  const contextClient = useContext(AuthClientContext);
  const client = contextClient ?? getAuthClient();
  const actions = useMemo(
    () => ({
      login: async (returnTo?: string, hints?: Pick<LoginOptions, 'uiLocales' | 'tenantHint' | 'loginHint'>) => {
        if (!client) throw new Error('No OIDC client: wrap the tree in <AuthProvider>');
        const target = returnTo ?? currentLocation();
        authStore.getState().setStatus('authenticating');
        await client.login({ ...hints, ...(target ? { returnTo: target } : {}) });
      },
      loginWithPassword: async (username: string, password: string) => {
        if (!client) throw new Error('No OIDC client: wrap the tree in <AuthProvider>');
        authStore.getState().setStatus('authenticating');
        try {
          const tokens = await client.loginWithPassword(username, password);
          authStore.getState().setTokens(tokens, client.getClaims());
          sessionHint.set();
          topLevelSilentGuard.reset();
          broadcastAuth({ type: 'signed-in' });
        } catch (err) {
          authStore.getState().setError(err instanceof OidcError ? err.code : err instanceof Error ? err.message : String(err));
          throw err;
        }
      },
      logout: async () => {
        broadcastAuth({ type: 'signed-out' });
        let redirected = false;
        if (client) {
          // Ending the identity provider's session needs the id token as a hint (otherwise the provider asks the
          // user to confirm, or keeps the session). A freshly opened /logout has none yet: restore it silently first.
          if (!client.getTokens() && sessionHint.present()) {
            await client.signInSilently().catch(() => undefined);
          }
          redirected = await client.logout().catch(() => false);
        }
        sessionHint.clear();
        topLevelSilentGuard.reset();
        authStore.getState().clear({ signedOut: true });
        return redirected;
      },
    }),
    [client],
  );
  return { ...state, ...actions, client };
}

/** The OIDC client provided by the nearest AuthProvider (or the global one). */
export function useAuthClient(): OidcClient | null {
  return useContext(AuthClientContext) ?? getAuthClient();
}
